import { useEffect } from 'react';
import { Link } from 'react-router';
import { CheckCircle2, Clock, Loader, Search, XCircle } from 'lucide-react';
import { formatDatum } from '../../lib/format.js';
import { oznacZamitnuteVidene, useMojeNalezy } from '../../lib/mojeNalezy.js';
import { EmptyState, Nacitani } from '../../components/ui.js';
import { bezUvozovek } from '../NalezyPage.js';

/**
 * MOJE NÁLEZY (terén, wireframe 13 §2 „mistr vidí jen stav svého nálezu"):
 * odesláno / řeší se / zamítnuto S DŮVODEM — zpětná vazba, bez které mistr
 * přestane hlásit. ŽÁDNÉ částky (zeď drží API, UI nic nedopočítává).
 */

/** Ikona stavu nálezu — vizuální kotva vedle textového stavu. */
function StavIkona({ stav }: { stav: string }) {
  const spolecne = { size: 18, strokeWidth: 2, 'aria-hidden': true } as const;
  if (stav === 'zamitnut') return <XCircle {...spolecne} className="shrink-0 text-error-text" />;
  if (stav === 'novy') return <Clock {...spolecne} className="shrink-0 text-text-secondary" />;
  if (stav === 'slouceno')
    return <CheckCircle2 {...spolecne} className="shrink-0 text-text-secondary" />;
  return <Loader {...spolecne} className="shrink-0 text-success-text" />;
}

export function MojeNalezyPage() {
  const { nalezy, nacitani } = useMojeNalezy(true);

  // Otevření stránky = zamítnuté viděny → tečka v Menu zhasne.
  useEffect(() => {
    if (!nalezy) return;
    const zamitnute = nalezy.filter((n) => n.stav === 'zamitnut').map((n) => n.id);
    if (zamitnute.length > 0) oznacZamitnuteVidene(zamitnute);
  }, [nalezy]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-bold">Moje nálezy</h1>
        <Link to="/teren/menu" className="min-h-12 py-3 font-semibold underline underline-offset-2">
          ‹ Menu
        </Link>
      </div>

      {nacitani && <Nacitani text="Načítám nálezy…" />}
      {nalezy && nalezy.length === 0 && (
        <EmptyState
          ikona={<Search size={22} strokeWidth={1.75} />}
          titulek="Zatím žádný nález"
          popis="Něco na stavbě nesedí? Nález je v deníku — foto + diktát, minuta."
        />
      )}

      <ul className="flex flex-col gap-3">
        {(nalezy ?? []).map((n) => (
          <li
            key={n.id}
            className={`rounded-xl border bg-surface p-4 shadow-sm ${
              n.stav === 'zamitnut' ? 'border-error-border' : 'border-border'
            }`}
          >
            <p className="text-sm text-text-secondary">
              {formatDatum(n.kdy)}
              {n.objekt ? ` · ${n.objekt.kod}` : ''}
            </p>
            <p className="mt-1 text-base">„{bezUvozovek(n.popis)}“</p>
            {/* Stav jako jeden textový uzel (vč. důvodu zamítnutí) + ikona. */}
            <p className="mt-2 flex items-start gap-2">
              <StavIkona stav={n.stav} />
              <span
                className={`min-w-0 text-base font-semibold ${
                  n.stav === 'zamitnut'
                    ? 'text-error-text'
                    : n.stav === 'novy'
                      ? 'text-text-primary'
                      : 'text-success-text'
                }`}
              >
                {n.stavText}
              </span>
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}
